import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { useGeneralUserSearch, useUpdateUser } from "./userUtils";
import { PersonAddAlt1, Delete } from "@mui/icons-material";
import { useUpdateMemberships } from "../Memberships/membershipUtils";
import { MembershipPreview } from "./EditUser";
import {
  ProgramMembership,
  ProgramRole,
  TraineeType,
  UpdateUserInput,
  User
} from "../../API";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useAllPrograms } from "../Programs/programUtils";
import {
  getProgramsCurrentUserCanAdminister,
  usersActiveProgramMemberships
} from "../../app/utils";
import { useDispatch } from "react-redux";
import {
  setShowToast,
  setToastMessage,
  setToastStyle
} from "../../common-ui/toastSlice";
import { UserFormErrors } from "../../app/constants";
import { LoadingButton } from "@mui/lab";
import { setShowModal } from "../../common-ui/modalSlice";
import { generateUUID } from "../../mocks/dataGenerators";
import { useWatch } from "react-hook-form";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";

type AddUserToProgramProps = {
  existingUsersWithEmail?: User[] | null;
  existingUsersWithNPI?: User[] | null;
  selectedProgramRole?: ProgramRole;
  selectedProgramTraineeType?: TraineeType;
  selectedNPI?: number;
  selectedInstitutionId?: string;
  selectedProgramId?: string;
  selectedSimplLearnId?: string;
  selectedPGY?: number;
  postSuccessAction?: () => void;
  isBulkAddition?: boolean;
  deleteRow?: () => void;
};

enum NewProgramRole {
  Clinician = "Clinician",
  ProgramAdmin = "ProgramAdmin"
}

type AddUserToProgramFormInputs = {
  traineeType?: TraineeType;
  role?: NewProgramRole;
  npi?: number;
  institutionId?: string;
  programId?: string;
  isClinicianTrainee?: boolean;
};

const isValidNPI = (npi?: number) =>
  npi && npi >= 1000000000 && npi <= 2999999999;

export const AddUserToProgram = ({
  existingUsersWithEmail,
  existingUsersWithNPI,
  selectedProgramRole,
  selectedProgramTraineeType,
  selectedNPI,
  selectedInstitutionId,
  selectedProgramId,
  selectedSimplLearnId,
  selectedPGY,
  postSuccessAction,
  isBulkAddition,
  deleteRow
}: AddUserToProgramProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showSimplLearnIdConflictDialog, setShowSimplLearnIdConflictDialog] =
    useState(false);
  const [duplicateTraineeRoleError, setDuplicateTraineeRoleError] =
    useState(false);
  const [availableRoles, setAvailableRoles] = useState<NewProgramRole[]>([]);
  const [
    selectedUserHasProposedMembership,
    setSelectedUserHasProposedMembership
  ] = useState(false);
  const [
    selectedUserHasDuplicateMembership,
    setSelectedUserHasDuplicateMembership
  ] = useState(false);
  const [incompatibleRolesError, setIncompatibleRolesError] = useState(false);

  // TODO: filter out sysadmins, maybe unregistered
  const { status: userSearchStatus, users } = useGeneralUserSearch(searchTerm);

  const { status: allProgramsStatus, programs: allPrograms } = useAllPrograms();

  const { status: updateMembershipsStatus, updateMemberships } =
    useUpdateMemberships();

  const { status: updateUserStatus, updateUser } = useUpdateUser();

  const {
    canAdministerMultipleInstitutions,
    canAdministerMultiplePrograms,
    programs: programsCurrentUserCanAdminister,
    institutions: institutionsCurrentUserCanAdminister
  } = getProgramsCurrentUserCanAdminister();

  const initialRole =
    selectedProgramRole &&
    (selectedProgramRole === ProgramRole.Trainee ||
      selectedProgramRole === ProgramRole.Attending)
      ? NewProgramRole.Clinician
      : selectedProgramRole === ProgramRole.ProgramAdmin
      ? NewProgramRole.ProgramAdmin
      : undefined;

  const defaultIsClinicianTrainee = selectedProgramRole === ProgramRole.Trainee;

  const { control, getValues, handleSubmit, watch, setValue, reset, trigger } =
    useForm<AddUserToProgramFormInputs>({
      defaultValues: {
        role: initialRole,
        traineeType: selectedProgramTraineeType,
        npi: selectedNPI,
        institutionId: selectedInstitutionId,
        programId: selectedProgramId,
        isClinicianTrainee: defaultIsClinicianTrainee
      }
    });
  const currentlySelectedRole = getValues("role") as NewProgramRole;
  const currentlySelectedNPI = getValues("npi");

  const rows =
    searchTerm.length < 4
      ? [
          ...(existingUsersWithEmail ? existingUsersWithEmail : []),
          ...(existingUsersWithNPI ? existingUsersWithNPI : [])
        ]
      : users;

  const selectedUser =
    rows.find((r) => r && r.id === selectedUserId) ?? undefined;
  const selectedUsersSimplLearnId = selectedUser?.simplLearnId;

  const hasSimplLearnIdConflict = !!(
    selectedUsersSimplLearnId &&
    selectedSimplLearnId &&
    selectedUsersSimplLearnId !== selectedSimplLearnId
  );

  const defaultProgramId = canAdministerMultiplePrograms
    ? ""
    : programsCurrentUserCanAdminister?.[0]?.id;

  const preSubmit: SubmitHandler<AddUserToProgramFormInputs> = (data) => {
    if (hasSimplLearnIdConflict) {
      setShowSimplLearnIdConflictDialog(true);
    } else {
      submitAddUserToProgram(data);
    }
  };

  const submitAddUserToProgram = async (
    data: AddUserToProgramFormInputs,
    overwriteSimplLearnId?: boolean
  ) => {
    try {
      if (selectedUser?.programMemberships?.items) {
        let targetProgramId = data.programId ?? defaultProgramId;
        let targetRole = data.role;
        let targetNPI = data.npi;
        let traineeType = data.traineeType;

        const memberships: MembershipPreview[] =
          selectedUser.programMemberships.items.flatMap((pm) =>
            pm ? [pm] : []
          );

        if (isBulkAddition) {
          targetProgramId = selectedProgramId;
          targetRole =
            selectedProgramRole === ProgramRole.Trainee ||
            selectedProgramRole === ProgramRole.Attending
              ? NewProgramRole.Clinician
              : NewProgramRole.ProgramAdmin;
          targetNPI = selectedNPI;
          traineeType = selectedProgramTraineeType;
        }
        let mappedProgramRole: ProgramRole;

        if (targetRole === NewProgramRole.Clinician) {
          mappedProgramRole = getValues("isClinicianTrainee")
            ? ProgramRole.Trainee
            : ProgramRole.Attending;
        } else if (targetRole === NewProgramRole.ProgramAdmin) {
          mappedProgramRole = ProgramRole.ProgramAdmin;
        } else {
          throw new Error("Unexpected role selected");
        }

        if (targetProgramId && targetRole) {
          memberships.push({
            id: generateUUID(),
            programId: targetProgramId,
            programRole: mappedProgramRole,
            programMemberId: selectedUser.id,
            traineeType: traineeType,
            __typename: "ProgramMembership"
          });

          await updateMemberships(selectedUser.id, memberships);

          const updateObject: UpdateUserInput = {
            id: selectedUser.id,
            pgy: selectedPGY
          };

          if (
            selectedSimplLearnId &&
            (overwriteSimplLearnId || !selectedUsersSimplLearnId)
          ) {
            updateObject.simplLearnId = selectedSimplLearnId;
          }

          if (
            targetRole === NewProgramRole.Clinician &&
            !getValues("isClinicianTrainee") &&
            targetNPI &&
            !selectedUser?.npi
          ) {
            updateObject.npi = data.npi;
          }

          if (
            updateObject.npi ||
            updateObject.simplLearnId ||
            updateObject.pgy
          ) {
            await updateUser(updateObject);
          }

          dispatch(setToastStyle("success"));
          dispatch(setToastMessage(t("Updated Program Memberships")));
          dispatch(setShowToast(true));
          if (postSuccessAction) {
            postSuccessAction();
          }
        } else {
          const errorString = "Error: missing targetProgramId or targetRole";
          console.error(errorString);
          throw new Error(errorString);
        }
      } else {
        const errorString = "Error: User has no programMemberships";
        console.error(errorString);
        throw new Error(errorString);
      }
    } catch (error) {
      console.error("Error updating program memberships:", error);
      dispatch(setToastStyle("error"));
      dispatch(setToastMessage(t("Failed to update Program Memberships")));
      dispatch(setShowToast(true));
    }
  };

  const watchers = watch(["role", "npi", "institutionId", "programId"]);

  const anyApiIsLoading = [
    updateMembershipsStatus,
    updateUserStatus,
    userSearchStatus,
    allProgramsStatus
  ].includes("loading");

  const updateSelectedUserHasProposedMembership = (selectedUser?: User) => {
    let foundConflict = false;
    let foundDuplicateMembership = false;

    if (selectedUser) {
      const roleForComparison = isBulkAddition
        ? selectedProgramRole
        : getValues("role") === NewProgramRole.ProgramAdmin
        ? ProgramRole.ProgramAdmin
        : getValues("isClinicianTrainee")
        ? ProgramRole.Trainee
        : ProgramRole.Attending;

      const programId = isBulkAddition
        ? selectedProgramId
        : getValues("programId") ?? defaultProgramId;

      const activeMemberships = usersActiveProgramMemberships(selectedUser);
      const membershipsInSameProgram = activeMemberships.filter(
        (membership) => membership.programId === programId
      );

      for (const membership of membershipsInSameProgram) {
        const existingRole = membership.programRole;

        const isAllowedCombo =
          (existingRole === ProgramRole.Attending &&
            roleForComparison === ProgramRole.ProgramAdmin) ||
          (existingRole === ProgramRole.ProgramAdmin &&
            roleForComparison === ProgramRole.Attending);

        if (existingRole === roleForComparison) {
          foundDuplicateMembership = true;
          break;
        }

        if (!isAllowedCombo) {
          foundConflict = true;
          break;
        }
      }
    }

    setSelectedUserHasProposedMembership(foundConflict);
    setSelectedUserHasDuplicateMembership(foundDuplicateMembership);
  };

  const columns: GridColDef[] = [
    {
      headerName: "",
      field: "radio",
      renderCell: (cellValues) => {
        return (
          <Radio
            checked={cellValues.id === selectedUserId}
            value={cellValues.id}
            name="radio-buttons"
          />
        );
      }
    },
    {
      headerName: t("First Name"),
      field: "firstName"
    },
    {
      headerName: t("Last Name"),
      field: "lastName"
    },
    {
      headerName: t("Email"),
      field: "email",
      flex: 1
    },
    {
      headerName: t("Institution"),
      field: "institution",
      renderCell: (cellValues) =>
        (cellValues?.row?.programMemberships?.items as ProgramMembership[])
          ?.filter((membership) => !membership.endDate)
          .map((pm) => (
            <>
              {
                allPrograms?.find((p) => pm.programId == p.id)?.institution
                  ?.name
              }
              <br />
            </>
          )),
      flex: 1
    },
    {
      headerName: t("Program"),
      field: "program",
      renderCell: (cellValues) =>
        (cellValues?.row?.programMemberships?.items as ProgramMembership[])
          ?.filter((membership) => !membership.endDate)
          .map((pm) => (
            <>
              {allPrograms?.find((p) => pm.programId == p.id)?.name}
              <br />
            </>
          )),
      flex: 1
    },
    ...(!isBulkAddition
      ? [
          {
            headerName: t("Training Program"),
            field: "trainingProgram",
            renderCell: (cellValues: GridRenderCellParams) =>
              (
                cellValues?.row?.programMemberships
                  ?.items as ProgramMembership[]
              )
                ?.filter(
                  (membership) =>
                    !membership.endDate && membership.programRole === "Trainee"
                )
                .map((pm, index) => (
                  <div key={index}>
                    {allPrograms?.find((p) => pm.programId === p.id)?.name}
                    <br />
                  </div>
                )),
            flex: 1
          }
        ]
      : []),
    {
      headerName: t("Role"),
      field: "role",
      renderCell: !isBulkAddition
        ? (cellValues) => {
            const memberships = (
              cellValues?.row?.programMemberships?.items as ProgramMembership[]
            )?.filter((membership) => !membership.endDate);

            const roleLabels = new Set<string>();

            let hasClinician = false;

            memberships?.forEach((pm) => {
              if (
                pm.programRole === ProgramRole.Trainee ||
                pm.programRole === ProgramRole.Attending
              ) {
                hasClinician = true;
              } else {
                roleLabels.add(pm.programRole);
              }
            });

            if (hasClinician) {
              roleLabels.add("Clinician");
            }

            return (
              <div>
                {Array.from(roleLabels).map((role, index) => (
                  <div key={index}>{role}</div>
                ))}
              </div>
            );
          }
        : (cellValues) =>
            (cellValues?.row?.programMemberships?.items as ProgramMembership[])
              ?.filter((membership) => !membership.endDate)
              .map((pm) => (
                <>
                  {pm.programRole}
                  <br />
                </>
              )),
      flex: 1
    },
    ...(!isBulkAddition
      ? [
          {
            headerName: t("Job Title"),
            field: "jobTitle",
            renderCell: (cellValues: GridRenderCellParams) => {
              const row = cellValues?.row;

              // Return jobTitle if it exists
              if (row?.jobTitle) {
                return row.jobTitle;
              }

              // Extract valid active memberships
              const memberships = (
                row?.programMemberships?.items as ProgramMembership[]
              )?.filter((membership) => !membership.endDate);

              if (!memberships?.length) return "N/A";

              // Extract programRoles from active memberships
              const roles: ProgramRole[] = memberships.map(
                (m) => m.programRole
              );

              // Define role priority
              const rolePriority: ProgramRole[] = [
                ProgramRole.Trainee,
                ProgramRole.Attending,
                ProgramRole.ProgramAdmin
              ];

              for (const priorityRole of rolePriority) {
                if (roles.includes(priorityRole)) {
                  return priorityRole;
                }
              }

              return roles[0] || "N/A";
            }
          }
        ]
      : []),
    {
      headerName: t("Status"),
      field: "status",
      renderCell: (cellValues) =>
        cellValues?.row?.eulaAcceptedDate ? (
          <Chip label={t("Registered")} />
        ) : (
          <Chip label={t("Unregistered")} color={"warning"} />
        ),
      flex: 1
    },
    {
      headerName: t("NPI"),
      field: "npi",
      flex: 1
    },
    {
      headerName: t("SIMPL LearnID"),
      field: "simplLearnId",
      flex: 1
    }
  ];

  const programsInSelectedInstitution =
    programsCurrentUserCanAdminister?.filter(
      (program) => program.institutionId === getValues("institutionId")
    );

  const showNPI =
    !!selectedUser &&
    getValues("role") === NewProgramRole.Clinician &&
    !getValues("isClinicianTrainee") &&
    !selectedUser?.npi;

  const editDisabled = !selectedUserId || anyApiIsLoading;

  const watchRole = useWatch({ control, name: "role" });
  const watchIsClinicianTrainee = useWatch({
    control,
    name: "isClinicianTrainee"
  });
  const watchProgramId = useWatch({ control, name: "programId" });

  const submitDisabled =
    !selectedUserId ||
    anyApiIsLoading ||
    duplicateTraineeRoleError ||
    incompatibleRolesError ||
    (!isBulkAddition && !watchRole) ||
    !selectedUser?.eulaAcceptedDate ||
    (showNPI && !isValidNPI(currentlySelectedNPI)) ||
    selectedUserHasProposedMembership ||
    selectedUserHasDuplicateMembership;

  useEffect(() => {
    trigger(["role", "isClinicianTrainee"]);
  }, [watchRole, watchIsClinicianTrainee]);

  useEffect(() => {
    reset();
  }, [selectedProgramRole, selectedProgramTraineeType, selectedNPI]);

  useEffect(() => {
    if (selectedUser) {
      const activeMemberships = usersActiveProgramMemberships(selectedUser);
      const userHasActiveTraineeMembership = activeMemberships.some(
        (membership) => membership.programRole === ProgramRole.Trainee
      );
      const addingTraineeRole = selectedProgramRole === ProgramRole.Trainee;
      const addingDuplicateTrainee =
        addingTraineeRole && userHasActiveTraineeMembership;

      setDuplicateTraineeRoleError(addingDuplicateTrainee);
      updateSelectedUserHasProposedMembership(selectedUser);
    }
  }, [currentlySelectedRole]);

  useEffect(() => {
    const selectedRole = getValues("role");
    if (!selectedRole && availableRoles.length > 0) {
      setValue("role", availableRoles[0]);
    }
  }, [availableRoles]);

  useEffect(() => {
    if (selectedUser) {
      const activeMemberships = usersActiveProgramMemberships(selectedUser);
      const userHasActiveTraineeMembership = activeMemberships.some(
        (membership) => membership.programRole === ProgramRole.Trainee
      );
      const userHasActiveProgramAdmin = activeMemberships.some(
        (membership) => membership.programRole === ProgramRole.ProgramAdmin
      );

      const incomingIsTrainee =
        watchRole === NewProgramRole.Clinician &&
        watchIsClinicianTrainee === true;
      const incomingIsProgramAdmin = watchRole === NewProgramRole.ProgramAdmin;

      const isAddingDuplicateTrainee =
        !!userHasActiveTraineeMembership && !!incomingIsTrainee;

      const isAddingIncompatible =
        (incomingIsTrainee && userHasActiveProgramAdmin) ||
        (incomingIsProgramAdmin && userHasActiveTraineeMembership);

      setDuplicateTraineeRoleError(isAddingDuplicateTrainee);
      setIncompatibleRolesError(isAddingIncompatible);
      updateSelectedUserHasProposedMembership(selectedUser);
    }
  }, [selectedUser, watchRole, watchIsClinicianTrainee, watchProgramId]);

  return (
    <>
      <Grid container spacing={2} style={{ padding: "0px 10px 10px 10px" }}>
        {existingUsersWithEmail && (
          <Grid item xs={12} sm={12}>
            <Alert severity="error" sx={{ marginTop: 1 }}>
              <div style={{ fontWeight: "bold" }}>
                {t(
                  "A user already exists on the system with this email address. "
                )}
              </div>
              {t(
                "If the information for the user is correct, select the user you would like to add to the program."
              )}
            </Alert>
          </Grid>
        )}
        {existingUsersWithNPI && (
          <Grid item xs={12} sm={12}>
            <Alert severity="error" sx={{ marginTop: 1 }}>
              <div style={{ fontWeight: "bold" }}>
                {t("A user already exists on the system with this NPI. ")}
              </div>
              {t(
                "If the information for the user is correct, select the user you would like to add to the program."
              )}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12} sm={12}>
          <div style={{ fontSize: "24px", fontWeight: "400" }}>
            {t("Add Existing User to Program")}
          </div>
          {!isBulkAddition &&
            t(
              "Search for  the existing user you would like to add to your program."
            )}
        </Grid>
        <Grid item xs={12} sm={12}>
          <form
            className="AddUserToProgramForm"
            onSubmit={handleSubmit(preSubmit)}
            noValidate
            role="form"
          >
            <Grid container spacing={2}>
              {!isBulkAddition && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Search"
                    placeholder="Name, email, NPI, etc."
                    onChange={(event) => {
                      setSearchTerm(event.target.value);
                    }}
                  />
                </Grid>
              )}

              {!searchTerm && (existingUsersWithEmail || existingUsersWithNPI) && (
                <Grid item xs={12} sm={12}>
                  {t("Suggested Users")}
                </Grid>
              )}
              <Grid item xs={12} sm={12}>
                <div style={{ height: "230px", overflow: "auto" }}>
                  <DataGrid
                    hideFooter
                    columns={columns}
                    rows={rows}
                    disableVirtualization
                    hideFooterPagination={true}
                    rowSelection={true}
                    loading={userSearchStatus == "loading"}
                    getRowHeight={({ model }) => {
                      const membershipItems = Array.isArray(
                        model?.programMemberships?.items
                      )
                        ? model.programMemberships.items
                        : [];
                      const membershipsSize = membershipItems.length || 1;
                      if (membershipsSize === 1) return 50;
                      return membershipsSize * 30;
                    }}
                    onRowSelectionModelChange={(ids) => {
                      if (ids[0]) {
                        setSelectedUserId(ids[0].toString());
                      } else {
                        setSelectedUserId(null);
                      }
                    }}
                  />
                </div>
              </Grid>

              <Grid item xs={12} sm={12}>
                <div>
                  Select the user you would like to add to your program.
                </div>
                {(existingUsersWithEmail || existingUsersWithNPI) &&
                  !isBulkAddition && (
                    <div>
                      If the suggested users are not who you are looking for,
                      use the Search bar above to find the user.
                    </div>
                  )}
              </Grid>

              {!isBulkAddition && canAdministerMultipleInstitutions && (
                <Grid item xs={12} sm={3}>
                  <Controller
                    control={control}
                    name="institutionId"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <FormControl fullWidth>
                        <InputLabel id="institution-label">
                          {t("Institution")}
                        </InputLabel>
                        <Select
                          id="institutionId"
                          label={t("Institution")}
                          labelId="institution-label"
                          onChange={onChange}
                          required
                          value={value}
                        >
                          {institutionsCurrentUserCanAdminister?.map((i) => (
                            <MenuItem value={i?.id} key={i?.id}>
                              {i?.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              )}

              {!isBulkAddition && canAdministerMultiplePrograms && (
                <Grid item xs={12} sm={3}>
                  <Controller
                    control={control}
                    name="programId"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <FormControl fullWidth>
                        <InputLabel id="program-label">
                          {t("Program")}
                        </InputLabel>
                        <Select
                          id="programId"
                          label={t("Program")}
                          labelId="program-label"
                          onChange={onChange}
                          required
                          value={value}
                        >
                          {programsInSelectedInstitution?.map((p) => (
                            <MenuItem value={p?.id} key={p?.id}>
                              {p?.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              )}

              {!isBulkAddition && (
                <Grid item xs={12} sm={3}>
                  <Controller
                    control={control}
                    name="role"
                    rules={{
                      required: true
                    }}
                    render={({ field: { onChange, onBlur, value } }) => {
                      return (
                        <FormControl fullWidth>
                          <InputLabel id="role-label">{t("Role")}</InputLabel>
                          <Select
                            label={t("Role")}
                            labelId="role-label"
                            id="role"
                            value={value}
                            onChange={(e) => {
                              onChange(e);
                              const selected = e.target.value;
                              if (selected !== NewProgramRole.Clinician) {
                                setValue("isClinicianTrainee", undefined);
                              } else {
                                if (
                                  getValues("isClinicianTrainee") === undefined
                                ) {
                                  setValue("isClinicianTrainee", false);
                                }
                              }
                            }}
                            disabled={editDisabled}
                          >
                            <MenuItem value={NewProgramRole.Clinician}>
                              Clinician
                            </MenuItem>
                            <MenuItem value={NewProgramRole.ProgramAdmin}>
                              Program Administrator
                            </MenuItem>
                          </Select>
                        </FormControl>
                      );
                    }}
                  />
                </Grid>
              )}

              {!isBulkAddition && watchRole === NewProgramRole.Clinician && (
                <Grid item xs={12} sm={3}>
                  <Controller
                    name="isClinicianTrainee"
                    control={control}
                    rules={{
                      validate: (value) =>
                        value !== undefined || "This field is required."
                    }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error }
                    }) => (
                      <FormControl
                        component="fieldset"
                        required
                        error={!!error}
                        fullWidth
                      >
                        <FormLabel component="legend" sx={{ mb: 1 }}>
                          {t(
                            "Is this user a trainee in your program about whom you would like to collect assessments?"
                          )}
                        </FormLabel>
                        <RadioGroup
                          row
                          value={
                            value === true ? "yes" : value === false ? "no" : ""
                          }
                          onChange={(e) => {
                            const selected = e.target.value === "yes";
                            onChange(selected);
                          }}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </Grid>
              )}

              {showNPI && (
                <Grid item xs={12} sm={3}>
                  <Controller
                    control={control}
                    name="npi"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <FormControl fullWidth>
                        <TextField
                          id="npi"
                          label={t("NPI")}
                          disabled={editDisabled}
                          onChange={onChange}
                          type="number"
                          required
                        />
                      </FormControl>
                    )}
                  />
                </Grid>
              )}

              {selectedUser && !selectedUser.eulaAcceptedDate && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ marginTop: 1 }}>
                    {t(
                      "An unregistered user's program membership cannot be edited."
                    )}
                  </Alert>
                </Grid>
              )}

              {selectedUserHasProposedMembership && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ marginTop: 1 }}>
                    {t(
                      "The selected user already has a conflicting role in this program."
                    )}
                  </Alert>
                </Grid>
              )}
              {selectedUserHasDuplicateMembership && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ marginTop: 1 }}>
                    {t(
                      UserFormErrors.AddExistingUserDuplicateMembership ??
                        "User is being added to the same program with the same role."
                    )}
                  </Alert>
                </Grid>
              )}
              {duplicateTraineeRoleError && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ marginTop: 1 }}>
                    {t(UserFormErrors.OneTraineeMembership)}
                  </Alert>
                </Grid>
              )}
              {incompatibleRolesError && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ marginTop: 1 }}>
                    {t(UserFormErrors.TraineeCannotBeProgramAdmin)}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12} container gap={2}>
                <Grid>
                  <LoadingButton
                    style={{ width: "300px" }}
                    variant="contained"
                    startIcon={<PersonAddAlt1 />}
                    disabled={submitDisabled}
                    loading={anyApiIsLoading}
                    type="submit"
                  >
                    {t("Add existing user to program")}
                  </LoadingButton>
                </Grid>
                {isBulkAddition && (
                  <Grid>
                    <Button
                      variant="contained"
                      startIcon={<Delete />}
                      color="error"
                      onClick={() => {
                        if (deleteRow) {
                          // Delete the row
                          deleteRow();

                          // Show a toast message
                          dispatch(setToastStyle("info"));
                          dispatch(setToastMessage(t("Row removed")));
                          dispatch(setShowToast(true));

                          // Close the window
                          dispatch(setShowModal(false));
                        }
                      }}
                    >
                      {t("Remove row from bulk invitation")}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
      <Dialog
        open={showSimplLearnIdConflictDialog}
        aria-labelledby="learn-id-conflict-title"
        aria-describedby="learn-id-conflict-description"
      >
        <DialogTitle id="learn-id-conflict-title">
          {t("SIMPL LearnID Error")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="learn-id-conflict-description">
            {t(
              "A SIMPL LearnID already exists for this user, and it doesn't match the one you entered. Are you sure you want to overwrite the original SIMPL LearnID?"
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowSimplLearnIdConflictDialog(false);
              submitAddUserToProgram(getValues());
            }}
          >
            {t("No - Keep the Original")}
          </Button>
          <Button
            onClick={() => {
              setShowSimplLearnIdConflictDialog(false);
              submitAddUserToProgram(getValues(), true);
            }}
          >
            {t("Yes - I need to overwrite the SIMPL LearnID")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
